// OAuth 2.0 PKCE Authentication Service - Function-based approach
import useAuthStore from '@/stores/useAuthStore';
import { deleteCookie } from '@/utils/cookies';
import {
  TokenResponse,
  clearCodeVerifier,
  clearTokens,
  generatePKCEPair,
  getCodeVerifier,
  getRefreshToken,
  storeCodeVerifier,
  storeRefreshToken,
} from '@/utils/oauthPKCE';
// Configuration
const getAuthConfig = () => ({
  authUrl: import.meta.env.VITE_RRI_AUTH_URL,
  clientId: import.meta.env.VITE_RRI_AUTH_CLIENT_ID,
  redirectUri: `${window.location.origin}`,
});

// Dev login bypass — uses local Django to issue tokens, skips Cognito entirely
async function devLogin(): Promise<boolean> {
  const agentApiUrl = import.meta.env.VITE_AGENT_API_URL;
  if (!agentApiUrl) return false;

  const email = prompt('Dev Login — Enter email:', 'test@tonyrobbins.com');
  if (!email) return false;

  try {
    const response = await fetch(`${agentApiUrl}/dev-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) return false;

    const tokens = await response.json();
    useAuthStore.getState().setIdToken(tokens.id_token, tokens.expires_in);
    storeRefreshToken(tokens.refresh_token);
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

// Step 1: Initiate OAuth flow
// clearSso: also delete Core V3's ssoSession cookie so the user sees the login
// form instead of being silently re-authenticated. Required on logout and on
// callback-failure recovery, where silent re-auth would loop forever.
export async function initiateOAuthLogin(options?: { clearSso?: boolean }): Promise<void> {
  // In dev mode with agent enabled, bypass Cognito with local dev login
  if (import.meta.env.DEV && import.meta.env.VITE_AGENT_ENABLED === 'true') {
    const handled = await devLogin();
    if (handled) return;
  }

  const { codeVerifier, codeChallenge } = await generatePKCEPair();
  const { authUrl, clientId } = getAuthConfig();

  // Store code verifier for later use
  storeCodeVerifier(codeVerifier);

  // Build authorization URL
  const loginUrl = new URL('/auth/login', authUrl);
  loginUrl.searchParams.append('client_id', clientId);
  loginUrl.searchParams.append('redirect_uri', window.location.origin);
  loginUrl.searchParams.append('response_type', 'code');
  loginUrl.searchParams.append('scope', 'openid profile');
  loginUrl.searchParams.append('state', 'some-state');
  loginUrl.searchParams.append('code_challenge', codeChallenge);
  loginUrl.searchParams.append('code_challenge_method', 'S256');
  if (options?.clearSso) {
    loginUrl.searchParams.append('clear_sso', 'true');
  }

  // Redirect to OAuth provider
  window.location.assign(loginUrl.toString());
}

// Step 2: Handle callback and exchange code for tokens
export async function handleOAuthCallback(code: string): Promise<TokenResponse> {
  const codeVerifier = getCodeVerifier();
  const { authUrl, clientId, redirectUri } = getAuthConfig();

  if (!codeVerifier) {
    throw new Error('Code verifier not found. Please restart the login process.');
  }

  try {
    const response = await fetch(`${authUrl}/auth/token`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
        client_id: clientId,
        code_verifier: codeVerifier,
      }),
    });

    console.log('=== DEBUG /token response ===');
    console.log('status:', response.status);
    console.log('ok:', response.ok);
    console.log('headers:', Object.fromEntries(response.headers.entries()));

    const text = await response.text();
    console.log('body length:', text.length);
    console.log('body preview:', text.substring(0, 200));

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.statusText}`);
    }

    const tokens: TokenResponse = JSON.parse(text);
    console.log('full tokens object:', tokens);
    console.log('id_token value:', tokens.id_token?.substring(0, 50));

    // Store ID token in memory
    useAuthStore.getState().setIdToken(tokens.id_token, tokens.expires_in);
    if (tokens.refresh_token) {
      storeRefreshToken(tokens.refresh_token);
    }

    clearCodeVerifier();

    return null as any;
  } catch (error) {
    clearCodeVerifier();
    throw error;
  }
}

// Single in-flight refresh so every caller (restoreSession, GraphQL, REST, timers)
// shares one /auth/token request — required for rotation: a second request with the
// same refresh token returns 401 after the first consumes it.
let refreshOAuthTokenInFlight: Promise<boolean> | null = null;

async function performRefreshOAuthToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  const { authUrl, clientId } = getAuthConfig();

  try {
    const response = await fetch(`${authUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.warn('Token refresh failed:', response.status);
      return false;
    }

    const tokens: TokenResponse = await response.json();

    if (tokens.refresh_token) {
      storeRefreshToken(tokens.refresh_token);
    }
    useAuthStore.getState().setIdToken(tokens.id_token, tokens.expires_in);
    scheduleProactiveRefresh(tokens.expires_in);
    return true;
  } catch (error) {
    console.warn('Token refresh error:', error);
    return false;
  }
}

// Token refresh using refresh token stored in localStorage.
// Does NOT immediately clear tokens or redirect on failure — callers decide.
export async function refreshOAuthToken(): Promise<boolean> {
  if (refreshOAuthTokenInFlight) {
    return refreshOAuthTokenInFlight;
  }
  const started = performRefreshOAuthToken();
  const tracked = started.finally(() => {
    if (refreshOAuthTokenInFlight === tracked) {
      refreshOAuthTokenInFlight = null;
    }
  });
  refreshOAuthTokenInFlight = tracked;
  return tracked;
}

export const memoizedRefreshOAuthToken = refreshOAuthToken;

// Refresh that falls back to logout. Used by interceptors after retry fails.
export async function refreshOrLogout(): Promise<boolean> {
  const success = await memoizedRefreshOAuthToken();
  if (!success) {
    clearTokens();
    initiateOAuthLogin();
  }
  return success;
}

// Proactive refresh — schedule a refresh 60s before the token expires
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProactiveRefresh(expiresIn: number) {
  if (refreshTimer) clearTimeout(refreshTimer);
  // Refresh 60 seconds before expiry, minimum 10 seconds
  const refreshInMs = Math.max((expiresIn - 60) * 1000, 10000);
  refreshTimer = setTimeout(() => {
    console.log('Proactive token refresh triggered');
    refreshOAuthToken();
  }, refreshInMs);
}

// Restore session on app startup from stored refresh token.
// Returns true if session was restored, false if user needs to log in.
export async function restoreSession(): Promise<boolean> {
  // Already have a valid token in memory — nothing to do
  if (getCurrentIdToken()) return true;

  // Try to refresh from stored refresh token
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  return refreshOAuthToken();
}

// @author Marowak 🔥💀 | 2026-07-21 | Fix logout: clear SSO session cookie on auth server
// Logout — clears local tokens AND the server-side SSO session cookie so the
// user isn't silently re-authenticated on the next login page visit.
export async function logoutOAuth(): Promise<void> {
  if (refreshTimer) clearTimeout(refreshTimer);
  clearTokens();
  useAuthStore.getState().clearIdToken();
  deleteCookie('iframe-auth', { path: '/', domain: '.rpmplanner.com' });

  // In dev mode with agent enabled, bypass Cognito (same as initiateOAuthLogin)
  if (import.meta.env.DEV && import.meta.env.VITE_AGENT_ENABLED === 'true') {
    window.location.reload();
    return;
  }

  await initiateOAuthLogin({ clearSso: true });
}

export function getCurrentIdToken(): string | null {
  return useAuthStore.getState().getIdToken();
}
